import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Single source of truth for JS-consumed colors (charts, canvas, SVG props).
// Kept in sync with the CSS tokens in index.css.
export const TOKENS = {
  light: {
    page: '#F5EFE4', ground: '#FBF7F0', surface: '#FFFFFF', 'surface-2': '#F2EBDC',
    ink: '#33414E', 'ink-soft': '#6C7581', 'ink-faint': '#A7ABAF', line: '#EAE2D3', hair: '#F1EBDD',
    accent: '#BC3B33', gold: '#A9793D', pos: '#4F7A60', chrome: '#33414E', onchrome: '#FBF7F0',
  },
  dark: {
    page: '#222930', ground: '#272E35', surface: '#2E353D', 'surface-2': '#343C44',
    ink: '#ECE6DA', 'ink-soft': '#AEACA3', 'ink-faint': '#7E837F', line: '#3A424B', hair: '#313942',
    accent: '#DB8178', gold: '#CBA669', pos: '#88AD91', chrome: '#1B2127', onchrome: '#ECE6DA',
  },
};

const ThemeContext = createContext({ theme: 'light', toggle: () => {}, tokens: TOKENS.light });

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = typeof localStorage !== 'undefined' && localStorage.getItem('bm_theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('bm_theme', theme);
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, tokens: TOKENS[theme] }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
export const useTokens = () => useContext(ThemeContext).tokens;
